import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EnhancedSnackbar, Spacer } from "@websential/cosmic-v20";
import { AuthService } from "../../services/AuthService";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import SecurityIcon from "@mui/icons-material/Security";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import { INITIAL_SNACKBAR_DATA } from "../../common/constants";
import { useMutation } from "@tanstack/react-query";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { AccountFormLayout } from "../AccountFormLayout";
import Box from "@mui/material/Box";

const AuthSelectionScreenForLoginComponent = () => {
	const navigate = useNavigate();
	const accessToken = localStorage.getItem("accessToken");
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [availableMethods, setAvailableMethods] = useState<string[]>([]);

	useEffect(() => {
		const fetchAvailableMethods = async () => {
			try {
				if (!accessToken) throw new Error("Access token missing");
				const response: any = await AuthService.GetAvailable2FAMethods(
					accessToken
				);
				if (response && response.twoFactorAuth === false) {
					localStorage.setItem("accessToken", response.accessToken);
					navigate("/auth-selection-screen");
				} else if (response && Array.isArray(response.twoFactorTypes)) {
					localStorage.setItem("accessToken", response.accessToken);
					setAvailableMethods(response.twoFactorTypes);
				}
			} catch (error: any) {
				setSnackbarData({
					open: true,
					message: error?.message || "Failed to load authentication methods",
					severity: "error",
				});
			}
		};

		fetchAvailableMethods();
	}, [accessToken]);

	const { mutate: triggerAuth } = useMutation({
		mutationFn: async (selectedAuthMethod: "phone" | "google" | "backup") => {
			if (!accessToken) throw new Error("Access token missing");

			if (selectedAuthMethod === "phone") {
				const response = await AuthService.CreateOtpByMessege(accessToken);
				if (
					response?.status === false &&
					response?.message === "Phone number not found add phone first"
				) {
					navigate("/add-phone-validation");
				} else {
					navigate("/phone-otp-validation", { state: accessToken });
				}
			} else if (selectedAuthMethod === "google") {
				navigate("/google-auth-validation", { state: { accessToken } });
			} else if (selectedAuthMethod === "backup") {
				navigate("/backup-code-validation", { state: { accessToken } });
			}
		},
		onError: (error: any) => {
			setSnackbarData({
				open: true,
				message: error?.message || "Something went wrong",
				severity: "error",
			});
		},
	});

	const handleSelect = (method: "phone" | "google" | "backup") => {
		triggerAuth(method);
	};

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") return;
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const renderAuthOption = (
		method: "phone" | "google" | "backup",
		IconComponent: any,
		title: string,
		description: string
	) => (
		<Grid key={method}>
			<Box
				onClick={() => handleSelect(method)}
				sx={{
					border: "1px solid #ccc",
					borderRadius: "25px",
					p: 2,
					display: "flex",
					alignItems: "center",
					width: "100%",
					cursor: "pointer",
					backgroundColor: "#fff",
					transition: "0.3s",
					"&:hover": {
						boxShadow: "0 0 0 2px rgb(25, 118, 210)",
					},
				}}
			>
				<IconComponent color="action" fontSize="medium" sx={{ mr: 4, ml: 2 }} />
				<Box>
					<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
						{title}
					</Typography>
					<Typography variant="body2" sx={{ color: "text.secondary" }}>
						{description}
					</Typography>
				</Box>
			</Box>
		</Grid>
	);

	return (
		<>
			<Grid container direction="column" alignItems="center" spacing={3}>
				<Grid width="100%">
					<Grid container spacing={2} direction="column">
						{availableMethods.includes("phone") &&
							renderAuthOption(
								"phone",
								PhoneAndroidIcon,
								"Phone",
								"Receive a code via SMS"
							)}
						{availableMethods.includes("google") &&
							renderAuthOption(
								"google",
								SecurityIcon,
								"Google Authenticator",
								"Use the Google Authenticator app"
							)}
						{availableMethods.includes("backup") &&
							renderAuthOption(
								"backup",
								TextSnippetIcon,
								"Backup Codes",
								"Enter one of your backup codes"
							)}
					</Grid>
				</Grid>
			</Grid>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId="login-form-snackbar"
				open={snackbarData.open}
				autoHide={true}
			/>
		</>
	);
};

export const AuthSelectionScreenForLoginForm = () => {
	return (
		<AccountFormLayout
			title="Choose verification method"
			subtitle="Already verified 2FA Authentication?"
			subtitleLinkText="Sign In."
			subtitleLinkNavigateTo="/login"
			form={<AuthSelectionScreenForLoginComponent />}
		/>
	);
};
